import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  MapPin, Calendar, Clock, Phone, Mail, User, 
  ChevronLeft, Navigation, ExternalLink, Star, Download, FileText
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { it } from "date-fns/locale";
import axios from "axios";
import SocialShare from "@/components/SocialShare";
import AddToCalendar from "@/components/AddToCalendar";
import ReviewsSection, { StarRating } from "@/components/ReviewsSection";
import SEOHead from "@/components/SEOHead";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SagraDetailPage = () => {
  const { slug } = useParams();
  const [sagra, setSagra] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchSagra();
  }, [slug]);

  const fetchSagra = async () => {
    try {
const response = await axios.get(`${API}/sagre/by-slug/${slug}`);
      setSagra(response.data);
    } catch (error) {
      setError("Sagra non trovata");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), "d MMMM yyyy", { locale: it });
    } catch {
      return dateStr;
    }
  };

  const openMaps = () => {
    if (sagra.coordinate) {
      window.open(`https://www.google.com/maps/search/?api=1&query=${sagra.coordinate.lat},${sagra.coordinate.lng}`, "_blank");
    } else {
      const address = encodeURIComponent(`${sagra.indirizzo}, ${sagra.comune}, ${sagra.provincia}`);
      window.open(`https://www.google.com/maps/search/?api=1&query=${address}`, "_blank");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background" data-testid="sagra-detail-loading">
        <SEOHead title="Caricamento... | Sagre Italia" />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="skeleton-gentle">
            <div className="h-6 w-24 bg-muted rounded mb-6" />
            <div className="aspect-[2/1] bg-muted rounded-xl mb-8" />
            <div className="h-8 w-2/3 bg-muted rounded mb-3" />
            <div className="h-4 w-1/3 bg-muted rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !sagra) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" data-testid="sagra-detail-error">
        <SEOHead title="Sagra non trovata | Sagre Italia" />
        <div className="text-center">
          <h1 className="text-xl font-medium text-foreground mb-4">Sagra non trovata</h1>
          <Link to="/cerca">
            <Button variant="outline" className="rounded-lg">Torna alla ricerca</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="sagra-detail-page">
      <SEOHead sagra={sagra} />
      
      {/* Back button */}
      <div className="max-w-4xl mx-auto px-4 py-4">
        <Link to="/cerca">
          <Button variant="ghost" size="sm" className="rounded-lg gap-1 -ml-2" data-testid="back-btn">
            <ChevronLeft className="w-4 h-4" />
            Indietro
          </Button>
        </Link>
      </div>

      <div className="max-w-4xl mx-auto px-4 pb-16">
        {/* Hero Image */}
        <div className="aspect-[2/1] rounded-xl overflow-hidden bg-muted mb-8" data-testid="sagra-image">
          <img
            src={sagra.immagine_url || "https://images.unsplash.com/photo-1504674900247-0877df9cc836"}
            alt={sagra.nome}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Header */}
        <div className="mb-8">
          {/* Tags */}
          {sagra.specialita && sagra.specialita.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {sagra.specialita.map((spec, i) => (
                <Badge key={i} variant="secondary" className="rounded-md text-xs font-normal">
                  {spec}
                </Badge>
              ))}
            </div>
          )}
          
          {/* Title */}
          <h1 className="text-3xl md:text-4xl text-foreground mb-3" data-testid="sagra-title">
            {sagra.nome}
          </h1>
          
          {/* Meta */}
          <div className="flex flex-wrap items-center gap-4 text-muted-foreground text-sm">
            <span className="flex items-center gap-1.5">
              <MapPin className="w-4 h-4" />
              {sagra.comune}, {sagra.provincia}
            </span>
            <span className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4" />
              {formatDate(sagra.data_inizio)}
              {sagra.data_fine !== sagra.data_inizio && ` — ${formatDate(sagra.data_fine)}`}
            </span>
            {sagra.valutazione_media > 0 && (
              <span className="flex items-center gap-1.5">
                <Star className="w-4 h-4 fill-secondary text-secondary" />
                {sagra.valutazione_media.toFixed(1)} ({sagra.numero_recensioni})
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2 mb-10">
          <Button onClick={openMaps} className="rounded-lg gap-1.5" data-testid="directions-btn">
            <Navigation className="w-4 h-4" />
            Indicazioni
          </Button>
          <AddToCalendar sagra={sagra} />
          <SocialShare sagra={sagra} />
          {sagra.locandina_filename && (
            <Button 
              variant="outline"
              className="rounded-lg gap-1.5" 
              onClick={() => window.open(`${API}/sagre/${sagra.id}/locandina`, '_blank')}
              data-testid="download-locandina-btn"
            >
              <Download className="w-4 h-4" />
              Scarica Locandina
            </Button>
          )}
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Description */}
            <div data-testid="description-section">
              <h2 className="text-xl text-foreground mb-3">Descrizione</h2>
              <p className="text-muted-foreground leading-relaxed">
                {sagra.descrizione}
              </p>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Info Card */}
            <div className="bg-muted/50 rounded-xl p-5 space-y-4">
              {/* Time */}
              {(sagra.orario_apertura || sagra.orario_chiusura) && (
                <div className="flex items-start gap-3">
                  <Clock className="w-4 h-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-foreground">Orari</p>
                    <p className="text-sm text-muted-foreground">
                      {sagra.orario_apertura} - {sagra.orario_chiusura}
                    </p>
                  </div>
                </div>
              )}

              {/* Location */}
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-foreground">Luogo</p>
                  <p className="text-sm text-muted-foreground">
                    {sagra.indirizzo}<br />{sagra.comune}, {sagra.provincia}
                  </p>
                </div>
              </div>

              {/* Map button */}
              <Button 
                variant="outline" 
                onClick={openMaps} 
                className="w-full rounded-lg gap-1.5"
                data-testid="open-maps-btn"
              >
                <ExternalLink className="w-4 h-4" />
                Apri in Maps
              </Button>
            </div>

            {/* Contact Card */}
            {sagra.contatti && (sagra.contatti.nome_organizzatore || sagra.contatti.telefono || sagra.contatti.email) && (
              <div className="bg-muted/50 rounded-xl p-5 space-y-4" data-testid="contacts-section">
                <p className="text-sm font-medium text-foreground">Contatti</p>
                
                {sagra.contatti.nome_organizzatore && (
                  <div className="flex items-center gap-3">
                    <User className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">{sagra.contatti.nome_organizzatore}</span>
                  </div>
                )}
                
                {sagra.contatti.telefono && (
                  <a href={`tel:${sagra.contatti.telefono}`} className="flex items-center gap-3 text-sm text-muted-foreground hover:text-foreground transition-colors">
                    <Phone className="w-4 h-4" />
                    {sagra.contatti.telefono}
                  </a>
                )}
                
                {sagra.contatti.email && (
                  <a href={`mailto:${sagra.contatti.email}`} className="flex items-center gap-3 text-sm text-muted-foreground hover:text-foreground transition-colors truncate">
                    <Mail className="w-4 h-4 flex-shrink-0" />
                    {sagra.contatti.email}
                  </a>
                )}
              </div>
            )}
          </div>
        </div>

       {/* Disclaimer */}
        <div className="border-t border-border pt-10 pb-8">
          <div className="bg-muted/30 border border-border/50 rounded-lg p-4 flex items-start gap-3">
            <div className="text-muted-foreground mt-0.5">ℹ️</div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Su <span className="font-semibold">SAGRE-OGGI.it</span> trovi una selezione di eventi basata su segnalazioni e ricerche online. 
              Poiché i programmi possono subire modifiche dell'ultimo minuto indipendenti dalla nostra volontà, 
              decliniamo ogni responsabilità su variazioni di date e servizi. Si consiglia di contattare l’ente organizzatore.
            </p>
          </div>
        </div>

        {/* Reviews */}
        <div className="pt-2">
          <h2 className="text-xl text-foreground mb-6">Recensioni</h2>
          <ReviewsSection 
            sagraId={sagra.id} 
            initialRating={sagra.valutazione_media || 0}
            initialCount={sagra.numero_recensioni || 0}
          />
        </div>
      </div>
    </div>
  );
};

export default SagraDetailPage;
